import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  scope: string;
}

interface AccountsResponse {
  accounts?: Array<{
    accountId: string;
    accountNumber: string;
    type?: string;
    name?: string;
    status?: string;
    positions?: Array<any>;
    [key: string]: any;
  }>;
  [key: string]: any;
}

export default function SchwabTokenDebugger() {
  const [code, setCode] = useState<string | null>(null);
  const [session, setSession] = useState<string | null>(null);
  const [token, setToken] = useState<TokenResponse | null>(null);
  const [accounts, setAccounts] = useState<AccountsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Parse URL on component mount
  useEffect(() => {
    const url = new URL(window.location.href);
    const codeParam = url.searchParams.get('code');
    const sessionParam = url.searchParams.get('session');
    
    if (codeParam) {
      setCode(codeParam);
      setSession(sessionParam);
      // Automatically exchange code for token
      exchangeCodeForToken(codeParam);
    } else {
      setError('No authorization code found in URL');
    }
  }, []);

  const exchangeCodeForToken = async (authCode: string) => {
    setLoading(true);
    setError(null);
    try {
      // Call the backend endpoint to exchange code for token
      const response = await fetch('/api/schwab/exchange-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: authCode })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to exchange code for token: ${response.status}`);
      }
      
      const tokenData = await response.json();
      console.log('Token received:', tokenData);
      setToken(tokenData);
      
      // If we got a token, fetch accounts immediately
      if (tokenData.access_token) {
        await getAccounts(tokenData.access_token);
      }
    } catch (err) {
      console.error('Error exchanging code for token:', err);
      setError(err instanceof Error ? err.message : 'Unknown error exchanging code for token');
    } finally {
      setLoading(false);
    }
  };

  const refreshAuthToken = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log("*** REFRESHING ACCESS TOKEN ***");
      
      if (!token?.refresh_token) {
        throw new Error("No refresh token available");
      }
      
      // Get client credentials from environment or config
      // In a real app, these should be securely retrieved from the backend
      const response = await fetch('/api/schwab/refresh-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refresh_token: token.refresh_token })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to refresh token: ${response.status}`);
      }
      
      const newTokenData = await response.json();
      console.log("New Refresh Token:", newTokenData.refresh_token ? '[REDACTED]' : 'Not provided');
      console.log("New Access Token:", newTokenData.access_token ? '[REDACTED]' : 'Not provided');
      
      setToken(newTokenData);
      return newTokenData;
    } catch (err) {
      console.error('Error refreshing auth token:', err);
      setError(err instanceof Error ? err.message : 'Unknown error refreshing token');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getAccounts = async (accessToken: string) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log("*** API TEST CALL: ACCOUNTS ***");
      
      const response = await fetch('https://api.schwabapi.com/trader/v1/accounts?fields=positions', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Accept': 'application/json',
          'Accept-Encoding': 'application/json',
        }
      });
      
      if (!response.ok) {
        // If unauthorized, try to refresh token
        if (response.status === 401 && token?.refresh_token) {
          const newToken = await refreshAuthToken();
          if (newToken?.access_token) {
            // Retry with new token
            return getAccounts(newToken.access_token);
          } else {
            throw new Error("Failed to refresh token");
          }
        }
        
        const errorText = await response.text();
        throw new Error(`Failed to fetch accounts: ${response.status} ${errorText}`);
      }
      
      const accountsData = await response.json();
      console.log('Accounts data:', accountsData);
      setAccounts(accountsData);
      return accountsData;
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError(err instanceof Error ? err.message : 'Unknown error fetching accounts');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshToken = async () => {
    try {
      await refreshAuthToken();
    } catch (err) {
      // Error already handled in refreshAuthToken
    }
  };

  const handleGetAccounts = async () => {
    try {
      if (token?.access_token) {
        await getAccounts(token.access_token);
      } else {
        setError('No access token available');
      }
    } catch (err) {
      // Error already handled in getAccounts
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Card className="w-full mb-6">
        <CardHeader>
          <CardTitle>Schwab Token Debugger</CardTitle>
          <CardDescription>Displaying code, token, and API response information</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-2">URL Parameters</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-md">
              <div>
                <span className="font-bold">Code:</span> {code ? 
                  <span className="font-mono break-all">{code}</span> : 
                  <span className="text-gray-500">Not found</span>
                }
              </div>
              <div>
                <span className="font-bold">Session:</span> {session ? 
                  <span className="font-mono break-all">{session}</span> : 
                  <span className="text-gray-500">Not found</span>
                }
              </div>
            </div>
          </div>

          {loading && (
            <div className="text-center py-4">
              <div className="animate-pulse text-blue-500">Loading...</div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4 text-red-700">
              <p className="font-bold">Error</p>
              <p>{error}</p>
            </div>
          )}

          {token && (
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">Access Token</h3>
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="mb-2">
                  <Badge variant="outline">Token Type: {token.token_type}</Badge>
                  <Badge variant="outline" className="ml-2">Expires In: {token.expires_in}s</Badge>
                  <Badge variant="outline" className="ml-2">Scope: {token.scope || 'Not specified'}</Badge>
                </div>
                <div className="mt-2">
                  <p className="font-bold mb-1">Access Token:</p>
                  <p className="font-mono text-xs break-all bg-black text-white p-2 rounded overflow-auto max-h-40">
                    {token.access_token}
                  </p>
                </div>
                <div className="mt-2">
                  <p className="font-bold mb-1">Refresh Token:</p>
                  <p className="font-mono text-xs break-all bg-black text-white p-2 rounded overflow-auto max-h-40">
                    {token.refresh_token}
                  </p>
                </div>
                <div className="mt-4 flex space-x-4">
                  <Button onClick={handleGetAccounts} disabled={loading}>
                    Fetch Accounts Using Token
                  </Button>
                  <Button onClick={handleRefreshToken} disabled={loading} variant="outline">
                    Refresh Token
                  </Button>
                </div>
              </div>
            </div>
          )}

          {accounts && (
            <div>
              <h3 className="text-lg font-semibold mb-2">Account Information</h3>
              
              {accounts.accounts && accounts.accounts.length > 0 ? (
                <div>
                  {accounts.accounts.map((account, index) => (
                    <div key={account.accountId || index} className="bg-gray-50 p-4 rounded-md mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold">{account.name || `Account ${index + 1}`}</h4>
                        <Badge>{account.accountNumber}</Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                        <div><span className="font-semibold">Account ID:</span> {account.accountId}</div>
                        <div><span className="font-semibold">Type:</span> {account.type || 'Not specified'}</div>
                        <div><span className="font-semibold">Status:</span> {account.status || 'Not specified'}</div>
                      </div>
                      
                      {account.positions && account.positions.length > 0 ? (
                        <div className="mt-4">
                          <h5 className="font-semibold mb-2">Positions</h5>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead>
                                <tr className="bg-gray-100">
                                  <th className="px-3 py-2 text-left">Symbol</th>
                                  <th className="px-3 py-2 text-left">Description</th>
                                  <th className="px-3 py-2 text-right">Quantity</th>
                                  <th className="px-3 py-2 text-right">Market Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {account.positions.map((position, idx) => (
                                  <tr key={`pos-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="px-3 py-2 font-medium">{position.symbol || 'N/A'}</td>
                                    <td className="px-3 py-2">{position.description || position.instrument?.description || 'N/A'}</td>
                                    <td className="px-3 py-2 text-right">{position.quantity?.toLocaleString() || 'N/A'}</td>
                                    <td className="px-3 py-2 text-right">
                                      {typeof position.marketValue === 'number' ? 
                                        `$${position.marketValue.toLocaleString(undefined, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2
                                        })}` : 'N/A'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 mt-2">No positions available for this account.</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No accounts found in the response.</p>
              )}
              
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-2">Raw API Response</h3>
                <pre className="bg-gray-800 text-gray-100 p-4 rounded-md overflow-auto max-h-96 text-xs">
                  {JSON.stringify(accounts, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}